// Sign in (SPEC §7.1). Wrong credentials give one message without saying which part was wrong.

import { fetchSchool, login } from "../../api/auth";
import type { ApiResult } from "../../api/client";
import type { LoginResult, SchoolInfo } from "../../api/types";
import { t } from "../../i18n/i18n";
import { navigate, type RouteMatch } from "../../router";
import { getUser, setSchool, startSession } from "../../session";
import { createElement, createHeading } from "../../ui/dom";
import { createErrorBox, showProblem } from "../../ui/feedback";
import { createInputField, type Field } from "../../ui/forms";

let afterSignIn: (returnPath: string) => void = (): void => undefined;

/** What happens after a successful sign-in (main.ts redraws the shell and navigates). */
export function onSignedIn(handler: (returnPath: string) => void): void {
  afterSignIn = handler;
}

export async function renderSignInPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  if (getUser() !== null) {
    // Already signed in (e.g. a bookmarked sign-in page): go where the sign-in would have led.
    navigate(chooseReturnPath(route));
    return;
  }

  const page: HTMLElement = createElement("main", "sign-in");
  const form: HTMLFormElement = createElement("form", "card");
  const username: Field<HTMLInputElement> = createInputField(t("signIn.username"), "text", "");
  const password: Field<HTMLInputElement> = createInputField(t("signIn.password"), "password", "");
  username.input.autocomplete = "username";
  password.input.autocomplete = "current-password";
  const errorBox: HTMLDivElement = createErrorBox();
  const submit: HTMLButtonElement = createElement("button", "button button-primary", t("signIn.submit"));
  submit.type = "submit";
  form.append(createHeading(1, t("signIn.title")), username.wrapper, password.wrapper, errorBox, submit);
  form.addEventListener("submit", (event: SubmitEvent): void => {
    event.preventDefault();
    void submitSignIn({ username, password, errorBox, submit }, chooseReturnPath(route));
  });
  page.append(form);
  container.replaceChildren(page);
  document.title = t("signIn.title");
  username.input.focus();
  return Promise.resolve();
}

interface SignInForm {
  username: Field<HTMLInputElement>;
  password: Field<HTMLInputElement>;
  errorBox: HTMLDivElement;
  submit: HTMLButtonElement;
}

async function submitSignIn(form: SignInForm, returnPath: string): Promise<void> {
  form.submit.disabled = true;
  const result: ApiResult<LoginResult> = await login(form.username.input.value, form.password.input.value);
  if (result.ok === false) {
    form.submit.disabled = false;
    showProblem(form.errorBox, result.problem);
    return;
  }

  startSession(result.data.token, result.data.user);
  const school: ApiResult<SchoolInfo> = await fetchSchool();
  if (school.ok) {
    setSchool(school.data);
  }

  afterSignIn(returnPath === "/sign-in" ? "/" : returnPath);
}

/** Where to go after signing in: the page that asked for it, never the sign-in page itself. */
export function chooseReturnPath(route: RouteMatch): string {
  const requested: string | null = route.query.get("return");
  return requested === null || requested.startsWith("/sign-in") ? "/" : requested;
}
